import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Header, Icon, Input, Message, Modal } from "semantic-ui-react"
import { handleClickForKeyboard } from "simplydo/core/accessibility"

import actions from "actions"
import api from "api"
import { useAppDispatch, useAppSelector } from "store"

type OrganisationChooserViewProps = {
  errorMessage: string
  searchByCode: () => void
  organisationSearching: boolean
}

export const OrganisationChooserView = ({
  errorMessage,
  searchByCode,
  organisationSearching,
}: OrganisationChooserViewProps) => {
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const searchCode = useAppSelector((state) => state.entry.organisationSearchCode)
  const onUpdateCode = useCallback(
    (code) => {
      dispatch(actions.entry.updateOrganisationSearchCode(code))
    },
    [dispatch],
  )

  const openChatwoot = useCallback(() => {
    if (window.$chatwoot) {
      window.$chatwoot.toggle()
    }
  }, [])

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      >
        <Modal.Header>What is my organisational code?</Modal.Header>
        <Modal.Content>
          <p>
            In order to sign up to SimplyDo you will need an organisational code. This is a unique code that identifies
            your organisation.
          </p>
          <p>
            If you don't know this code then{" "}
            <span style={{ textDecorationLine: "underline", cursor: "pointer" }} onClick={() => openChatwoot()}>
              click here
            </span>{" "}
            or press the button below to start a chat with us. Tell us which organisation you are looking to join and we
            can support you to get started.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setHelpModalOpen(false)}>Close</Button>
          <Button onClick={() => openChatwoot()} primary>
            <Icon name="chat" />
            Speak to us
          </Button>
        </Modal.Actions>
      </Modal>

      <Header as="h2">
        Welcome to SimplyDo
        <Header.Subheader>{t("entry.register.org.title")}</Header.Subheader>
      </Header>

      {errorMessage && <Message warning>{errorMessage}</Message>}

      <div style={{ display: "flex", gap: 10 }}>
        <Input
          style={{ flex: 1 }}
          label=".simplydo.co.uk"
          labelPosition="right"
          name="orgCode"
          placeholder={t("entry.register.org.placeholder")}
          value={searchCode}
          onKeyDown={(e) => e.keyCode === 13 && searchByCode()}
          onChange={(e) => onUpdateCode(e.target.value)}
        />

        <Button loading={organisationSearching} disabled={!searchCode} primary onClick={searchByCode}>
          {t("generic.continue")}
          <Icon name="arrow right" />
        </Button>
      </div>
      <div style={{ marginTop: 15, marginBottom: 10, textAlign: "right" }}>
        <a
          href="#"
          role="button"
          tabIndex={0}
          onClick={() => setHelpModalOpen(true)}
          onKeyDown={handleClickForKeyboard(() => setHelpModalOpen(true))}
        >
          {t("entry.register.unknownCode")}
        </a>
      </div>
    </>
  )
}

const OrganisationChooser = () => {
  const { t } = useTranslation()
  const searchCode = useAppSelector((state) => state.entry.organisationSearchCode)
  const organisationSearching = useAppSelector((state) => state.entry.organisationSearching)
  const errorMessage = useAppSelector((state) => state.entry.error)

  const dispatch = useAppDispatch()
  const onSelectOrganisation = useCallback(
    (org) => {
      dispatch(actions.organisations.receiveUrlOrganisation(org))
    },
    [dispatch],
  )
  const onUpdateSearching = useCallback(
    (l) => {
      dispatch(actions.entry.updateOrganisationSearching(l))
    },
    [dispatch],
  )
  const onError = useCallback(
    (e) => {
      dispatch(actions.entry.updateError(e))
    },
    [dispatch],
  )

  const searchByCode = () => {
    onUpdateSearching(true)
    onError("")
    api.organisations.getByCode(
      searchCode,
      (organisation) => {
        onSelectOrganisation(organisation)
        onUpdateSearching(false)
      },
      () => {
        onError(t("entry.register.org.error"))
        onUpdateSearching(false)
      },
    )
  }

  return (
    <OrganisationChooserView
      errorMessage={errorMessage}
      searchByCode={searchByCode}
      organisationSearching={organisationSearching}
    />
  )
}

export default OrganisationChooser
